import { computed } from 'mobx';

import type { TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { FacebookCampaign } from './campaigns';
import type { IDimensionDefinition } from './creatives_base';
import { CreativeClass } from './creatives_base';
import type { IFacebookAdPreviewRequest, IFacebookDisplayCreative } from './creatives_meta';
import {
  createMetaDimensionValidator,
  FacebookDisplayCreative,
  ValidFacebookVideoDimensions,
} from './creatives_meta';

// =============================================================================
// Facebook Video Creative Types
// =============================================================================

export const VALID_META_VIDEO_FORMATS = ['video/mp4', 'video/quicktime'];
export const MIN_META_VIDEO_DURATION = 1;
// 241 minutes
export const MAX_META_VIDEO_DURATION = 14460;

export interface IFacebookVideoAdPreviewRequest extends IFacebookAdPreviewRequest {
  ad_video_id: string;
  ad_video_thumbnail: string;
}

export interface IFacebookVideoCreative extends IFacebookDisplayCreative {
  readonly _cls: CreativeClass.FacebookVideo;
  skippable: boolean;
  ad_video_id: string;
  ad_video_thumbnail: string;
  duration?: number;
}

// =============================================================================
// Facebook Video Creative Class
// =============================================================================

/**
 * FacebookVideoCreative handles Meta/Facebook video creatives.
 * Supports MP4 and MOV formats with Facebook-specific dimensions and duration constraints.
 */
export class FacebookVideoCreative extends FacebookDisplayCreative<IFacebookVideoCreative> {
  public override readonly className = 'FacebookVideoCreative';

  public override get constraints(): TConstraints<IFacebookVideoCreative> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [CreativeClass.FacebookVideo],
      },
      mimetype: {
        inclusion: {
          within: VALID_META_VIDEO_FORMATS,
          message: '^File must be MP4 or MOV format',
        },
      },
      duration: (_: string, attributes: Partial<IFacebookVideoCreative>) => {
        if (attributes.mimetype?.includes('video')) {
          return {
            numericality: {
              greaterThanOrEqualTo: MIN_META_VIDEO_DURATION,
              lessThanOrEqualTo: MAX_META_VIDEO_DURATION,
              message: `^Video duration must be between ${MIN_META_VIDEO_DURATION} second and ${Math.floor(MAX_META_VIDEO_DURATION / 60)} minutes`,
            },
          };
        }
        return {
          presence: {
            allowEmpty: true,
          },
        };
      },
      ad_video_thumbnail: {
        url: true,
      },
      ad_video_id: {
        presence: {
          allowEmpty: false,
        },
      },
      width: {
        ...super.constraints.width,
        equality: createMetaDimensionValidator(ValidFacebookVideoDimensions, 'width'),
      },
      height: {
        ...super.constraints.height,
        equality: createMetaDimensionValidator(ValidFacebookVideoDimensions, 'height'),
      },
    };
  }

  @computed
  public get hasStoredThumbnail(): boolean {
    return !!this.get('ad_video_thumbnail');
  }

  public override getDefaults(): Partial<IFacebookVideoCreative> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.FacebookVideo,
    };
  }

  public override getSpec(): IDimensionDefinition | undefined {
    const key =
      Object.keys(ValidFacebookVideoDimensions).find(
        (dimensionKey) => ValidFacebookVideoDimensions[dimensionKey].spec === this.get('spec'),
      ) ?? `${this.get('width')}x${this.get('height')}`;
    if (ValidFacebookVideoDimensions.hasOwnProperty(key)) {
      return ValidFacebookVideoDimensions[key];
    }
    return undefined;
  }

  public override getValidDimensions(): Record<string, IDimensionDefinition> {
    return ValidFacebookVideoDimensions;
  }

  public async getFacebookAdPreview(adFormat: string, campaign: FacebookCampaign): Promise<string> {
    this.assertCollection(this.collection);

    const attributes: IFacebookVideoAdPreviewRequest = {
      ad_format: adFormat,
      _cls: this.get('_cls'),
      facebook_page_id: campaign.get('facebook_page_id'),
      primary_text: this.get('primary_text'),
      name: this.get('name'),
      headlines: this.get('headlines'),
      display_link: this.get('display_link'),
      description: this.get('description'),
      url: this.get('url'),
      destination_url: this.get('destination_url') || campaign.get('destination_url'),
      call_to_action_type: this.get('call_to_action_type'),
      ad_video_id: this.get('ad_video_id'),
      ad_video_thumbnail: this.get('ad_video_thumbnail'),
    };

    const url = `${this.collection.getHostname()}integrations/facebook/ad_preview`;
    const response = await wretch<{ body: string }>(url, {
      method: 'POST',
      body: JSON.stringify(attributes),
      headers: this.collection.getHeaders(),
    });
    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data.body;
  }
}
